import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { PageStatusCircle } from '@/components/PageStatusCircle';
import { Box } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import DeleteButton from '@/components/DeleteButton';
import { useTranslation } from 'react-i18next';
import Styles from '@/assets/js/Styles';
import { Status } from '@/declarations/models/Status';
import { isApiTimestampAtOrAfter } from '@/utils/dates';
import { Draggable } from '@/declarations/Draggable';
import { PageListItem, PageListManualBlock } from '@/declarations/models/blocks/PageListBlocks';
import { Api } from '@/services/Api';
import { Page } from '@/declarations/models/Page';
import { EventsManualBlock } from '@/declarations/models/blocks/EventsBlocks';
import { Container } from '@mui/system';
import LockIcon from '@mui/icons-material/Lock';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DraggableCollapseContainer from '../../../../components/DraggableCollapseContainer';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import PageListManualBlockItemHeader from './PageListManualBlockItemHeader';
import PageListManualBlockItemSkinSelector from './PageListManualBlockItemSkinSelector';
import PageListManualBlockItemContent from './PageListManualBlockItemContent';
import { useEditorEventHandler, useEditorEventManager } from '../../../lib/eventManager/EditorEventManager';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';

export interface PageListManualBlockItemProps {
  index: number;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onDelete: (index: number) => void;
  color?: string;
  hideTitleInput?: boolean;
  overrideItemPath?: string;
  hideHighlightCheckbox?: boolean;
  hideSkinSelector?: boolean;
  disableReordering?: boolean;
}

type ItemPath = `${BlockPath}.items.${number}`;

type DisplayStatus = Status | 'planned_publish';

const getDisplayStatus = (page: Page): DisplayStatus => {
  if (page.status === Status.PUBLISHED) {
    const isPlannedPublish = !!page.published_at && isApiTimestampAtOrAfter(page.published_at);

    if (isPlannedPublish) {
      return 'planned_publish';
    }
  }

  return page.status;
};

const getDisplayStatusText = (displayStatus: DisplayStatus, tCommon: (key: string) => string): string => {
  if (displayStatus === 'planned_publish') {
    return tCommon('Status.planned_publishing');
  }

  if (displayStatus === Status.PUBLISHED) {
    return tCommon('Status.published');
  }

  if (displayStatus === Status.DRAFT) {
    return tCommon('Status.not_published');
  }

  if (displayStatus === Status.ARCHIVED) {
    return tCommon('Status.archived');
  }

  return '';
};

export default function PageListManualBlockItem({
  index,
  onReorder,
  onDelete,
  color,
  hideTitleInput,
  overrideItemPath,
  hideHighlightCheckbox = false,
  hideSkinSelector = false,
  disableReordering = false,
}: PropsWithChildren<PageListManualBlockItemProps>) {
  const { blockPath } = useCurrentBlock();
  const { t: tCommon } = useTranslation('common');
  const eventManager = useEditorEventManager();
  const [page, setPage] = useState<Page>();
  const itemPath: ItemPath = (overrideItemPath as ItemPath) || `${blockPath}.items.${index}`;

  const item = useWatch<BlockSpecificPage<PageListManualBlock | EventsManualBlock>, ItemPath>({
    name: itemPath,
  }) as PageListItem;

  const currentPageSiteId = useWatch<Page, `site_id`>({
    name: `site_id`,
  });

  const displayStatus = useMemo(() => (page ? getDisplayStatus(page) : undefined), [page]);
  const pageStatusText = useMemo(() => {
    if (!displayStatus) {
      return '';
    }

    return getDisplayStatusText(displayStatus, tCommon);
  }, [displayStatus, tCommon]);

  let statusColor: string;

  if (displayStatus === 'planned_publish') {
    statusColor = Styles.Colors.STRONG_ORANGE;
  } else if (displayStatus === Status.PUBLISHED) {
    statusColor = 'green';
  } else {
    statusColor = 'textSecondary';
  }

  useEffect(() => {
    if (item?.id) {
      const ctx = Api.getPageSimple(item.id);
      ctx.fetchDirect(undefined).then(setPage).finally(ctx.abort);
    }
  }, [item.id]);

  let imgMimeType = '';
  if (page?.image?.mimetype?.includes('png')) {
    imgMimeType = `mediaType=${page?.image?.mimetype}`;
  }

  return (
    <DraggableCollapseContainer
      useEventHandler={useEditorEventHandler}
      useEventManager={eventManager}
      disabled={disableReordering}
      color={color}
      unmountOnExit
      type={Draggable.PAGE_LIST_ITEM}
      imageThumbnail={
        page?.image ? (
          <img
            src={`${page?.image.url}?dimension=250x250&${imgMimeType}`}
            alt={page.title}
            style={{
              height: '50px',
              aspectRatio: '1/1',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div style={{ width: '50px', height: '50px' }} />
        )
      }
      // eslint-disable-next-line react/no-unstable-nested-components
      headerContent={(open) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <PageListManualBlockItemHeader
            overrideExpandIcon={
              <Box sx={{ p: 1, ml: 2 }}>
                <PageStatusCircle status={displayStatus} />
              </Box>
            }
            title={page?.title}
            siteName={currentPageSiteId === page?.site_id ? undefined : page?.site_name}
            secondaryTitle={item.local?.title}
            open={open}
          />

          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {page?.is_protected === true && (
              <LockIcon
                sx={{
                  fontSize: '14px',
                  color: page.locked_by_parent ? 'text.disabled' : 'inherit',
                  opacity: page.locked_by_parent ? 0.7 : 1,
                }}
              />
            )}

            {page?.status === Status.PUBLISHED &&
              !!page?.published_at &&
              isApiTimestampAtOrAfter(page.published_at) && <AccessTimeIcon fontSize='small' />}
          </Box>
        </Box>
      )}
      index={index}
      onReorder={onReorder}
      secondaryAction={
        <>
          {!hideSkinSelector && <PageListManualBlockItemSkinSelector path={`${itemPath}.local`} />}
          <DeleteButton
            onConfirm={() => onDelete(index)}
            component='IconButton'
            tooltip={tCommon('remove')}
            deleteIcon={<MaterialSymbol name='do_not_disturb_on' />}
          />
        </>
      }>
      <PageListManualBlockItemContent
        hideTitleInput={hideTitleInput}
        index={index}
        blockPath={blockPath}
        overrideItemPath={overrideItemPath}
        hideHighlightCheckbox={hideHighlightCheckbox}
      />
    </DraggableCollapseContainer>
  );
}
